<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useRightPanelResize } from 'dashboard/composables/useRightPanelResize';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { panelStyle, onResizeStart, resetPanelWidth } = useRightPanelResize();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('wizardAssistants/getRecords');
const uiFlags = useMapGetter('wizardAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat = useMapGetter('getSelectedChat');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const selectedCopilotThreadId = ref(null);
const messages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](
    selectedCopilotThreadId.value
  )
);
const rememberedThreadByConversationId = ref({});
const lastOpenedConversationId = ref(null);
const recentThreads = computed(() =>
  store.getters['copilotThreads/getRecords'].filter(
    thread => thread.conversation_id === currentChat.value?.id
  )
);
const activeThread = computed(() =>
  recentThreads.value.find(
    thread => thread.id === selectedCopilotThreadId.value
  )
);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_wizard_assistant_id;

  // If the user has selected a specific assistant, it takes first preference for Copilot.
  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    // Return the preferred assistant if found, otherwise continue to next cases
    if (preferredAssistant) return preferredAssistant;
  }

  // If the above is not available, the assistant connected to the inbox takes preference.
  if (inboxAssistant.value) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }
  // If neither of the above is available, the first assistant in the account takes preference.
  return assistants.value[0];
});

const closeCopilotPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_copilot_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
      is_sage_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedCopilotThreadId.value = null;
  await updateUISettings({
    preferred_wizard_assistant_id: assistant.id,
  });
};

const shouldShowCopilotPanel = computed(() => {
  const isWizardEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.WIZARD
  );
  const { is_copilot_panel_open: isCopilotPanelOpen } = uiSettings.value;
  return isWizardEnabled && isCopilotPanelOpen && !uiFlags.value.fetchingList;
});
const isCopilotPanelOpen = computed(
  () => uiSettings.value?.is_copilot_panel_open && shouldShowCopilotPanel.value
);

const setSelectedThreadId = threadId => {
  selectedCopilotThreadId.value = threadId;

  if (currentChat.value?.id) {
    rememberedThreadByConversationId.value = {
      ...rememberedThreadByConversationId.value,
      [currentChat.value.id]: threadId,
    };
  }
};

const handleReset = () => {
  setSelectedThreadId(null);
};

const loadRecentThreads = async () => {
  if (!currentChat.value?.id) return [];

  return store.dispatch('copilotThreads/get', {
    conversation_id: currentChat.value.id,
  });
};

const selectThread = async thread => {
  if (!thread?.id) return;

  try {
    setSelectedThreadId(thread.id);

    if (
      thread.assistant?.id &&
      thread.assistant.id !== activeAssistant.value?.id
    ) {
      await updateUISettings({
        preferred_wizard_assistant_id: thread.assistant.id,
      });
    }

    await store.dispatch('copilotMessages/get', thread.id);
  } catch (error) {
    useAlert(error.message);
  }
};

const sendMessage = async message => {
  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      setSelectedThreadId(response.id);
    }
  } catch (error) {
    useAlert(error.message);
  }
};

onMounted(() => {
  store.dispatch('wizardAssistants/get');
  loadRecentThreads();
});

watch(
  () => currentChat.value?.id,
  () => {
    selectedCopilotThreadId.value = null;
    loadRecentThreads();
  }
);

watch(isCopilotPanelOpen, async isOpen => {
  if (!isOpen || !currentChat.value?.id) return;

  const currentConversationId = currentChat.value.id;
  const previousOpenedConversationId = lastOpenedConversationId.value;
  const threads = await loadRecentThreads();

  if (
    currentConversationId === previousOpenedConversationId &&
    !selectedCopilotThreadId.value
  ) {
    const rememberedThreadId =
      rememberedThreadByConversationId.value[currentConversationId];
    const hasRememberedPreference = Object.prototype.hasOwnProperty.call(
      rememberedThreadByConversationId.value,
      currentConversationId
    );
    const threadToResume = hasRememberedPreference
      ? threads.find(thread => thread.id === rememberedThreadId)
      : threads[0];

    if (threadToResume) {
      await selectThread(threadToResume);
    }
  }

  lastOpenedConversationId.value = currentConversationId;
});
</script>

<template>
  <div
    v-if="shouldShowCopilotPanel"
    v-on-click-outside="() => closeCopilotPanel()"
    class="relative bg-n-surface-2 h-full shrink-0 flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full md:w-auto max-w-sm md:max-w-none transition-transform duration-300 ease-in-out md:relative ltr:border-l rtl:border-r border-n-weak shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowCopilotPanel,
        'md:hidden': !shouldShowCopilotPanel,
      },
    ]"
    :style="isSmallScreen ? undefined : panelStyle"
  >
    <div
      v-if="!isSmallScreen"
      class="absolute top-0 bottom-0 z-50 w-4 cursor-col-resize ltr:left-0 rtl:right-0"
      @pointerdown.stop.prevent="onResizeStart"
      @mousedown.stop.prevent="onResizeStart"
      @touchstart.stop.prevent="onResizeStart"
      @dblclick.stop="resetPanelWidth"
    >
      <div
        class="absolute top-1/2 flex h-16 w-3 -translate-y-1/2 items-center justify-center rounded-full border border-n-weak bg-n-surface-1 shadow-sm transition-colors hover:border-n-strong hover:bg-n-alpha-1 ltr:left-0.5 rtl:right-0.5"
      >
        <div class="flex flex-col gap-1">
          <span class="h-1 w-1 rounded-full bg-n-slate-10" />
          <span class="h-1 w-1 rounded-full bg-n-slate-10" />
          <span class="h-1 w-1 rounded-full bg-n-slate-10" />
        </div>
      </div>
    </div>
    <div class="flex h-full flex-col overflow-hidden">
      <Copilot
        :messages="messages"
        :support-agent="currentUser"
        :conversation-inbox-type="conversationInboxType"
        :assistants="assistants"
        :active-assistant="activeAssistant"
        :recent-threads="recentThreads"
        :active-thread-id="selectedCopilotThreadId"
        :active-thread-title="activeThread?.title || ''"
        :active-thread-assistant-name="activeThread?.assistant?.name || ''"
        :active-thread-created-at="activeThread?.created_at || null"
        :active-thread-has-summary="activeThread?.summary_present || false"
        @set-assistant="setAssistant"
        @send-message="sendMessage"
        @reset="handleReset"
        @select-thread="selectThread"
      />
    </div>
  </div>
  <template v-else />
</template>
